import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Cookies from 'js-cookie';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const SearchByName = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    checkindate: '',
  });
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    checkindate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [showBookingSelection, setShowBookingSelection] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorDetails, setErrorDetails] = useState({
    message: '',
    friendly_message: '',
    issues: [],
    today: '',
  });
  const [hotelInfo, setHotelInfo] = useState(null);
  const [manualDateInput, setManualDateInput] = useState('');
  const [guestData, setGuestData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Get hotel info from local storage
    const hotelPropertyCache = localStorage.getItem('hotelPropertyCache');
    if (hotelPropertyCache) {
      try {
        const parsedData = JSON.parse(hotelPropertyCache);
        setHotelInfo(parsedData.data);
      } catch (e) {
        console.error('Error parsing hotel property cache', e);
      }
    }

    // Fetch guest data for email verification only
    const fetchGuestData = async () => {
      const data = await getGuestData();
      if (data) {
        setGuestData(data);
      }
    };

    fetchGuestData();
  }, []);

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  const decryptUserData = async (encryptedData) => {
    try {
      if (encryptedData.email && encryptedData.email.length < 50) {
        return encryptedData;
      }

      const accessToken = Cookies.get('access_token');
      if (!accessToken) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          encrypted_fields: {
            email: encryptedData.email,
            phone_number: encryptedData.phone_number,
            country_code: encryptedData.country_code,
          },
        }),
      });

      if (response.status === 401) {
        return encryptedData;
      }

      if (!response.ok) {
        throw new Error(`Decryption failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Decryption error:', error);
      return encryptedData;
    }
  };

  const getGuestData = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) return null;

    try {
      const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
      if (!guestId) return null;

      const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!profileResponse.ok) {
        throw new Error(`Profile API Error: ${profileResponse.statusText}`);
      }

      const profileData = await profileResponse.json();
      const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

      if (isDecrypted) {
        return {
          email: profileData.emailid || '',
        };
      }

      const decryptedFields = await decryptUserData({
        email: profileData.emailid,
      });

      return {
        email: decryptedFields.email || profileData.emailid || '',
      };
    } catch (error) {
      console.error('Error fetching guest data:', error);
      return null;
    }
  };

  const validateName = (name) => {
    const regex = /^[a-zA-Z\s'-]+$/;
    if (!regex.test(name)) {
      return 'Only letters, spaces, hyphens, and apostrophes are allowed';
    }
    if (/\d/.test(name)) {
      return 'Numbers are not allowed in names';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (email.trim() === '') {
      return 'This field is required';
    }
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!regex.test(email)) {
      return 'Invalid email format (e.g., example@domain.com)';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'firstName' || name === 'lastName') {
      const nameError = validateName(value);
      if (nameError && value) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: nameError,
        }));
        return;
      }

      setFormErrors((prev) => ({
        ...prev,
        [name]: value.trim() === '' ? 'This field is required' : '',
      }));
    }

    if (name === 'email') {
      setFormErrors((prev) => ({
        ...prev,
        [name]: validateEmail(value),
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateInputChange = (e) => {
    const value = e.target.value;
    setManualDateInput(value);

    if (value.length === 10) {
      const parts = value.split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          const date = new Date(year, month, day);
          if (!isNaN(date.getTime())) {
            handleDateChange(date);
            return;
          }
        }
      }

      setFormErrors((prev) => ({
        ...prev,
        checkindate: 'Please enter a valid date in DD-MM-YYYY format',
      }));
    } else if (value.length > 0) {
      setFormErrors((prev) => ({
        ...prev,
        checkindate: 'Date must be in DD-MM-YYYY format',
      }));
    } else {
      setFormErrors((prev) => ({
        ...prev,
        checkindate: 'This field is required',
      }));
    }
  };

  const handleDateChange = (date) => {
    if (!date) {
      setFormData((prev) => ({
        ...prev,
        checkindate: '',
      }));
      setManualDateInput('');
      setFormErrors((prev) => ({
        ...prev,
        checkindate: 'This field is required',
      }));
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      setFormErrors((prev) => ({
        ...prev,
        checkindate: 'Check-in date cannot be in the past',
      }));
      return;
    }

    const nextYear = new Date();
    nextYear.setFullYear(today.getFullYear() + 1);

    if (date > nextYear) {
      setFormErrors((prev) => ({
        ...prev,
        checkindate: 'Check-in date cannot be more than 1 year in the future',
      }));
      return;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    setManualDateInput(`${day}-${month}-${year}`);
    const dateString = `${year}-${month}-${day}`;

    setFormData((prev) => ({
      ...prev,
      checkindate: dateString,
    }));

    setFormErrors((prev) => ({
      ...prev,
      checkindate: '',
    }));
  };

  const validateForm = () => {
    const errors = {
      firstName:
        formData.firstName.trim() === ''
          ? 'This field is required'
          : validateName(formData.firstName),
      lastName:
        formData.lastName.trim() === ''
          ? 'This field is required'
          : validateName(formData.lastName),
      email: validateEmail(formData.email),
      checkindate:
        formData.checkindate.trim() === '' ? 'This field is required' : formErrors.checkindate,
    };

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error !== '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Verify email matches logged-in account if applicable
    if (guestData && guestData.email && formData.email !== guestData.email) {
      setErrorDetails({
        message: 'Email does not match your account',
        friendly_message: 'Please use the email associated with your account',
        issues: [],
        today: new Date().toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
      });
      setShowErrorPopup(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/pre-checkin-by-name/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          checkin_date: formData.checkindate,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setErrorDetails({
            message: data.detail || "We couldn't find your reservation",
            friendly_message: 'Please verify your details and try again',
            issues: [],
            today: new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          });
          setShowErrorPopup(true);
        } else if (data.detail) {
          setErrorDetails({
            message: data.detail.message || data.detail,
            friendly_message: data.detail.friendly_message || 'Please check your details',
            issues: data.detail.issues || [],
            today:
              data.detail.today ||
              new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
          });
          setShowErrorPopup(true);
        } else {
          throw new Error(data.message || 'Failed to fetch bookings');
        }
        return;
      }

      if (!data.success) {
        throw new Error(data.message || 'No bookings found');
      }

      setBookings(data);

      if (data.multiple_rooms) {
        setShowBookingSelection(true);
      } else if (data.bookings.length === 1) {
        handleBookingSelection(data.bookings[0], data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSelection = (booking, fullResponse = bookings) => {
    const bookingDetails = {
      success: fullResponse.success,
      message: fullResponse.message,
      today: fullResponse.today,
      order_id: booking.order_id,
      primary_guest: fullResponse.primary_guest,
      bookings: fullResponse.bookings,
      multiple_rooms: fullResponse.multiple_rooms,
      attention_needed: fullResponse.attention_needed,
      primaryBooking: booking,
    };

    localStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
    sessionStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));

    if (booking.booking_status === 'Hard') {
      navigate(`/reservation/options/booking/welcome?bookingId=${booking.booking_id}`);
    } else {
      navigate('/checkin-details', { state: { booking } });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const today = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(today.getFullYear() + 1);

  return (
    <div className="min-h-1 bg-white p-3 relative">
      <h1 className="text-2xl text-center font-bold font-poppins mb-6">Find Your Reservation</h1>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-xl mx-auto">
        <div>
          <label className="block text-lg font-medium font-poppins mb-1">First Name*</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            onBlur={() => {
              setFormErrors((prev) => ({
                ...prev,
                firstName:
                  formData.firstName.trim() === ''
                    ? 'This field is required'
                    : validateName(formData.firstName),
              }));
            }}
            placeholder="Enter your first name"
            className="w-full border border-gray-300 rounded-md p-3 text-base"
            required
            minLength={2}
            pattern="[a-zA-Z\s'-]+"
            title="Only letters, spaces, hyphens, and apostrophes are allowed"
          />
          <div className="h-2">
            {formErrors.firstName && (
              <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium font-poppins mb-1">Last Name*</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            onBlur={() => {
              setFormErrors((prev) => ({
                ...prev,
                lastName:
                  formData.lastName.trim() === ''
                    ? 'This field is required'
                    : validateName(formData.lastName),
              }));
            }}
            placeholder="Enter your last name"
            className="w-full border border-gray-300 rounded-md p-3 text-base"
            required
            minLength={2}
            pattern="[a-zA-Z\s'-]+"
            title="Only letters, spaces, hyphens, and apostrophes are allowed"
          />
          <div className="h-2">
            {formErrors.lastName && (
              <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium font-poppins mb-1">Email Address*</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={() => {
              setFormErrors((prev) => ({
                ...prev,
                email: validateEmail(formData.email),
              }));
            }}
            placeholder="Enter the email used for booking"
            className="w-full border border-gray-300 rounded-md p-3 text-base"
            required
          />
          <div className="h-2">
            {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
          </div>
        </div>

        <div>
          <label className="block text-lg font-medium font-poppins mb-1">Check-in Date*</label>
          <div className="relative">
            <DatePicker
              selected={formData.checkindate ? new Date(formData.checkindate) : null}
              onChange={handleDateChange}
              minDate={today}
              maxDate={nextYear}
              placeholderText="Select check-in date"
              className="w-full border border-gray-300 rounded-md p-3 text-base"
              required
              dateFormat="dd-MM-yyyy"
              dropdownMode="select"
              customInput={
                <input
                  type="text"
                  value={manualDateInput}
                  onChange={handleDateInputChange}
                  placeholder="DD-MM-YYYY"
                  className="w-full border border-gray-300 rounded-md p-3 text-base"
                />
              }
            />
          </div>
          <div className="h-2">
            {formErrors.checkindate && (
              <p className="text-red-500 text-xs mt-1">{formErrors.checkindate}</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="p-3 rounded-md w-full bg-black font-poppins text-white hover:bg-gray-800 transition-colors disabled:bg-gray-400"
          disabled={loading}
        >
          {loading ? 'Searching...' : 'Search Reservation'}
        </button>
      </form>

      {error && (
        <div className="max-w-xl mx-auto mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {showErrorPopup && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl border border-gray-100 animate-fade-in">
            <div className="flex justify-between items-start gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-2 rounded-lg bg-red-100 text-red-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {errorDetails.message.includes('Email does not match')
                      ? 'Email Verification Failed'
                      : 'Reservation Not Found'}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">{errorDetails.today}</p>
                </div>
              </div>
              <button
                onClick={() => setShowErrorPopup(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-50"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                <h3 className="font-semibold text-red-800">{errorDetails.message}</h3>
                <p className="text-red-700 text-sm mt-1">{errorDetails.friendly_message}</p>
                {errorDetails.message.includes('Email does not match') && (
                  <p className="text-red-700 text-sm mt-2">
                    Your account is registered with: <strong>{guestData?.email}</strong>
                  </p>
                )}
              </div>

              {errorDetails.issues.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-500"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Booking Details
                  </h4>
                  <ul className="space-y-3">
                    {errorDetails.issues.map((issue, index) => (
                      <li key={index} className="flex gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <div className="h-2 w-2 rounded-full bg-gray-400"></div>
                        </div>
                        <div>
                          <p className="text-gray-800 text-sm font-medium">{issue.message}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{issue.friendly_message}</p>
                          {issue.checkin_date && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3.5 w-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <span>Check-in: {formatDate(issue.checkin_date)}</span>
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Need assistance? Contact our support team at
                <a
                  href={`mailto:${hotelInfo?.email || 'support@example.com'}`}
                  className="text-blue-600 hover:underline ml-1"
                >
                  {hotelInfo?.email || 'support@example.com'}
                </a>
                {hotelInfo?.phone && (
                  <span>
                    {' '}
                    or call us at{' '}
                    <a href={`tel:${hotelInfo.phone}`} className="text-blue-600 hover:underline">
                      {hotelInfo.phone}
                    </a>
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {showBookingSelection && bookings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Select Your Booking</h2>
                <p className="text-gray-600 mt-1">
                  {bookings.primary_guest} • {bookings.bookings[0].guest_email}
                </p>
              </div>
              <button
                onClick={() => setShowBookingSelection(false)}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              {bookings.bookings.map((booking) => (
                <div
                  key={booking.booking_id}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all duration-200 hover:border-blue-300"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                          Reservation Number : {booking.order_id}
                        </span>
                      </div>
                      <h3 className="font-semibold text-lg text-gray-800">
                        {booking.room_type.name}
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        {formatDate(booking.checkin_date)} - {formatDate(booking.checkout_date)} •{' '}
                        {booking.length_of_stay} night(s)
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Guests</p>
                        <p className="font-medium">{booking.number_of_guests}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="font-medium">{booking.status_message}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleBookingSelection(booking)}
                    className="mt-4 w-full p-2 bg-black text-white rounded-md hover:text-gray-300 transition-colors"
                  >
                    Select This Booking
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                Please select the booking you want to check in for
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchByName;

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const SearchByName = () => {
//   const [formData, setFormData] = useState({
//     firstName: "",
//     lastName: "",
//     email: "",
//     checkindate: "",
//   });
//   const [formErrors, setFormErrors] = useState({
//     firstName: "",
//     lastName: "",
//     email: "",
//     checkindate: "",
//   });
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [bookings, setBookings] = useState(null);
//   const [showBookingSelection, setShowBookingSelection] = useState(false);
//   const [showErrorPopup, setShowErrorPopup] = useState(false);
//   const [errorDetails, setErrorDetails] = useState({
//     message: "",
//     friendly_message: "",
//     issues: [],
//     today: "",
//   });
//   const [hotelInfo, setHotelInfo] = useState(null);
//   const [manualDateInput, setManualDateInput] = useState("");
//   const navigate = useNavigate();

//   useEffect(() => {
//     // Get hotel info from local storage
//     const hotelPropertyCache = localStorage.getItem("hotelPropertyCache");
//     if (hotelPropertyCache) {
//       try {
//         const parsedData = JSON.parse(hotelPropertyCache);
//         setHotelInfo(parsedData.data);
//       } catch (e) {
//         console.error("Error parsing hotel property cache", e);
//       }
//     }
//   }, []);

//   const validateName = (name) => {
//     const regex = /^[a-zA-Z\s'-]+$/;
//     if (!regex.test(name)) {
//       return "Only letters, spaces, hyphens, and apostrophes are allowed";
//     }
//     if (/\d/.test(name)) {
//       return "Numbers are not allowed in names";
//     }
//     return "";
//   };

//   const validateEmail = (email) => {
//     if (email.trim() === "") {
//       return "This field is required";
//     }
//     const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     if (!regex.test(email)) {
//       return "Invalid email format (e.g., example@domain.com)";
//     }
//     return "";
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;

//     // Validation for firstName and lastName
//     if (name === "firstName" || name === "lastName") {
//       const nameError = validateName(value);
//       if (nameError && value) {
//         setFormErrors((prev) => ({
//           ...prev,
//           [name]: nameError,
//         }));
//         return; // Don't update if contains invalid characters
//       }

//       setFormErrors((prev) => ({
//         ...prev,
//         [name]: value.trim() === "" ? "This field is required" : "",
//       }));
//     }

//     // Validation for email
//     if (name === "email") {
//       setFormErrors((prev) => ({
//         ...prev,
//         [name]: validateEmail(value),
//       }));
//     }

//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const handleDateInputChange = (e) => {
//     const value = e.target.value;
//     setManualDateInput(value);

//     // Try to parse the date when input is complete
//     if (value.length === 10) {
//       const parts = value.split("-");
//       if (parts.length === 3) {
//         const day = parseInt(parts[0], 10);
//         const month = parseInt(parts[1], 10) - 1;
//         const year = parseInt(parts[2], 10);

//         if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
//           const date = new Date(year, month, day);
//           if (!isNaN(date.getTime())) {
//             handleDateChange(date);
//             return;
//           }
//         }
//       }

//       // If we get here, the date is invalid
//       setFormErrors((prev) => ({
//         ...prev,
//         checkindate: "Please enter a valid date in DD-MM-YYYY format",
//       }));
//     } else if (value.length > 0) {
//       setFormErrors((prev) => ({
//         ...prev,
//         checkindate: "Date must be in DD-MM-YYYY format",
//       }));
//     } else {
//       setFormErrors((prev) => ({
//         ...prev,
//         checkindate: "This field is required",
//       }));
//     }
//   };

//   const handleDateChange = (date) => {
//     if (!date) {
//       setFormData((prev) => ({
//         ...prev,
//         checkindate: "",
//       }));
//       setManualDateInput("");
//       setFormErrors((prev) => ({
//         ...prev,
//         checkindate: "This field is required",
//       }));
//       return;
//     }

//     // Validate the date is not in the past
//     const today = new Date();
//     today.setHours(0, 0, 0, 0);

//     if (date < today) {
//       setFormErrors((prev) => ({
//         ...prev,
//         checkindate: "Check-in date cannot be in the past",
//       }));
//       return;
//     }

//     // Validate the date is not too far in the future (1 year)
//     const nextYear = new Date();
//     nextYear.setFullYear(today.getFullYear() + 1);

//     if (date > nextYear) {
//       setFormErrors((prev) => ({
//         ...prev,
//         checkindate: "Check-in date cannot be more than 1 year in the future",
//       }));
//       return;
//     }

//     // Format the date as DD-MM-YYYY for display and YYYY-MM-DD for storage
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = date.getFullYear();

//     setManualDateInput(`${day}-${month}-${year}`);

//     // Store in YYYY-MM-DD format for the API
//     const dateString = `${year}-${month}-${day}`;

//     setFormData((prev) => ({
//       ...prev,
//       checkindate: dateString,
//     }));

//     setFormErrors((prev) => ({
//       ...prev,
//       checkindate: "",
//     }));
//   };

//   const validateForm = () => {
//     const errors = {
//       firstName:
//         formData.firstName.trim() === ""
//           ? "This field is required"
//           : validateName(formData.firstName),
//       lastName:
//         formData.lastName.trim() === ""
//           ? "This field is required"
//           : validateName(formData.lastName),
//       email: validateEmail(formData.email),
//       checkindate:
//         formData.checkindate.trim() === ""
//           ? "This field is required"
//           : formErrors.checkindate, // Keep any existing date error
//     };

//     setFormErrors(errors);
//     return !Object.values(errors).some((error) => error !== "");
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/pre-checkin-by-name/`,
//         {
//           method: "POST",
//           headers: {
//             accept: "application/json",
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({
//             first_name: formData.firstName,
//             last_name: formData.lastName,
//             email: formData.email,
//             checkin_date: formData.checkindate,
//           }),
//         }
//       );

//       const data = await response.json();

//       if (!response.ok) {
//         if (response.status === 404) {
//           setErrorDetails({
//             message: data.detail || "We couldn't find your reservation",
//             friendly_message: "Please verify your details and try again",
//             issues: [],
//             today: new Date().toLocaleDateString("en-US", {
//               weekday: "long",
//               year: "numeric",
//               month: "long",
//               day: "numeric",
//             }),
//           });
//           setShowErrorPopup(true);
//         } else if (data.detail) {
//           setErrorDetails({
//             message: data.detail.message || data.detail,
//             friendly_message:
//               data.detail.friendly_message || "Please check your details",
//             issues: data.detail.issues || [],
//             today:
//               data.detail.today ||
//               new Date().toLocaleDateString("en-US", {
//                 weekday: "long",
//                 year: "numeric",
//                 month: "long",
//                 day: "numeric",
//               }),
//           });
//           setShowErrorPopup(true);
//         } else {
//           throw new Error(data.message || "Failed to fetch bookings");
//         }
//         return;
//       }

//       if (!data.success) {
//         throw new Error(data.message || "No bookings found");
//       }

//       setBookings(data);

//       if (data.multiple_rooms) {
//         setShowBookingSelection(true);
//       } else if (data.bookings.length === 1) {
//         handleBookingSelection(data.bookings[0], data);
//       }
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleBookingSelection = (booking, fullResponse = bookings) => {
//     const bookingDetails = {
//       success: fullResponse.success,
//       message: fullResponse.message,
//       today: fullResponse.today,
//       order_id: booking.order_id,
//       primary_guest: fullResponse.primary_guest,
//       bookings: fullResponse.bookings,
//       multiple_rooms: fullResponse.multiple_rooms,
//       attention_needed: fullResponse.attention_needed,
//       primaryBooking: booking,
//     };

//     localStorage.setItem("bookingDetails", JSON.stringify(bookingDetails));
//     sessionStorage.setItem("bookingDetails", JSON.stringify(bookingDetails));

//     if (booking.booking_status === "Hard") {
//       navigate(
//         `/reservation/options/booking/welcome?bookingId=${booking.booking_id}`
//       );
//     } else {
//       navigate("/checkin-details", { state: { booking } });
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return "";

//     // Handle both YYYY-MM-DD and other formats
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) {
//       // If direct parsing fails, try splitting (for YYYY-MM-DD format)
//       const [year, month, day] = dateString.split("-");
//       return `${day}-${month}-${year}`;
//     }

//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = date.getFullYear();
//     return `${day}-${month}-${year}`;
//   };

//   // Date range restrictions
//   const today = new Date();
//   const nextYear = new Date();
//   nextYear.setFullYear(today.getFullYear() + 1);

//   return (
//     <div className="min-h-1 bg-white p-3 relative">
//       <h1 className="text-2xl text-center font-bold font-poppins mb-6">
//         Find Your Reservation
//       </h1>

//       <form onSubmit={handleSubmit} className="space-y-4 max-w-xl mx-auto">
//         <div>
//           <label className="block text-lg font-medium font-poppins mb-1">
//             First Name*
//           </label>
//           <input
//             type="text"
//             name="firstName"
//             value={formData.firstName}
//             onChange={handleChange}
//             onBlur={() => {
//               setFormErrors((prev) => ({
//                 ...prev,
//                 firstName:
//                   formData.firstName.trim() === ""
//                     ? "This field is required"
//                     : validateName(formData.firstName),
//               }));
//             }}
//             placeholder="Enter your first name"
//             className="w-full border border-gray-300 rounded-md p-3 text-base"
//             required
//             minLength={2}
//             pattern="[a-zA-Z\s'-]+"
//             title="Only letters, spaces, hyphens, and apostrophes are allowed"
//           />
//           <div className="h-2">
//             {formErrors.firstName && (
//               <p className="text-red-500 text-xs mt-1">
//                 {formErrors.firstName}
//               </p>
//             )}
//           </div>
//         </div>

//         <div>
//           <label className="block text-lg font-medium font-poppins mb-1">
//             Last Name*
//           </label>
//           <input
//             type="text"
//             name="lastName"
//             value={formData.lastName}
//             onChange={handleChange}
//             onBlur={() => {
//               setFormErrors((prev) => ({
//                 ...prev,
//                 lastName:
//                   formData.lastName.trim() === ""
//                     ? "This field is required"
//                     : validateName(formData.lastName),
//               }));
//             }}
//             placeholder="Enter your last name"
//             className="w-full border border-gray-300 rounded-md p-3 text-base"
//             required
//             minLength={2}
//             pattern="[a-zA-Z\s'-]+"
//             title="Only letters, spaces, hyphens, and apostrophes are allowed"
//           />
//           <div className="h-2">
//             {formErrors.lastName && (
//               <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
//             )}
//           </div>
//         </div>

//         <div>
//           <label className="block text-lg font-medium font-poppins mb-1">
//             Email Address*
//           </label>
//           <input
//             type="email"
//             name="email"
//             value={formData.email}
//             onChange={handleChange}
//             onBlur={() => {
//               setFormErrors((prev) => ({
//                 ...prev,
//                 email: validateEmail(formData.email),
//               }));
//             }}
//             placeholder="Enter the email used for booking"
//             className="w-full border border-gray-300 rounded-md p-3 text-base"
//             required
//           />
//           <div className="h-2">
//             {formErrors.email && (
//               <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
//             )}
//           </div>
//         </div>

//         <div>
//           <label className="block text-lg font-medium font-poppins mb-1">
//             Check-in Date*
//           </label>
//           <div className="relative">
//             <DatePicker
//               selected={
//                 formData.checkindate ? new Date(formData.checkindate) : null
//               }
//               onChange={handleDateChange}
//               minDate={today}
//               maxDate={nextYear}
//               placeholderText="Select check-in date"
//               className="w-full border border-gray-300 rounded-md p-3 text-base"
//               required
//               dateFormat="dd-MM-yyyy"
//               dropdownMode="select"
//               customInput={
//                 <input
//                   type="text"
//                   value={manualDateInput}
//                   onChange={handleDateInputChange}
//                   placeholder="DD-MM-YYYY"
//                   className="w-full border border-gray-300 rounded-md p-3 text-base"
//                 />
//               }
//             />
//           </div>
//           <div className="h-2">
//             {formErrors.checkindate && (
//               <p className="text-red-500 text-xs mt-1">
//                 {formErrors.checkindate}
//               </p>
//             )}
//           </div>
//         </div>

//         <button
//           type="submit"
//           className="p-3 rounded-md w-full bg-black font-poppins text-white hover:bg-gray-800 transition-colors disabled:bg-gray-400"
//           disabled={loading}
//         >
//           {loading ? "Searching..." : "Search Reservation"}
//         </button>
//       </form>

//       {error && (
//         <div className="max-w-xl mx-auto mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
//           {error}
//         </div>
//       )}

//       {/* Error Popup */}
//       {showErrorPopup && (
//         <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl border border-gray-100 animate-fade-in">
//             {/* Header */}
//             <div className="flex justify-between items-start gap-4">
//               <div className="flex items-start gap-3">
//                 <div className="mt-0.5 p-2 rounded-lg bg-red-100 text-red-600">
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     className="h-6 w-6"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                     stroke="currentColor"
//                     strokeWidth={2}
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
//                     />
//                   </svg>
//                 </div>
//                 <div>
//                   <h2 className="text-xl font-bold text-gray-900">
//                     Reservation Not Found
//                   </h2>
//                   <p className="text-gray-500 text-sm mt-1">
//                     {errorDetails.today}
//                   </p>
//                 </div>
//               </div>
//               <button
//                 onClick={() => setShowErrorPopup(false)}
//                 className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-50"
//                 aria-label="Close"
//               >
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   className="h-5 w-5"
//                   viewBox="0 0 20 20"
//                   fill="currentColor"
//                 >
//                   <path
//                     fillRule="evenodd"
//                     d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
//                     clipRule="evenodd"
//                   />
//                 </svg>
//               </button>
//             </div>

//             {/* Main Content */}
//             <div className="mt-6 space-y-4">
//               {/* Error Message */}
//               <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
//                 <h3 className="font-semibold text-red-800">
//                   {errorDetails.message}
//                 </h3>
//                 <p className="text-red-700 text-sm mt-1">
//                   {errorDetails.friendly_message}
//                 </p>
//               </div>

//               {/* Issues List */}
//               {errorDetails.issues.length > 0 && (
//                 <div className="bg-gray-50 rounded-lg p-4">
//                   <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
//                     <svg
//                       xmlns="http://www.w3.org/2000/svg"
//                       className="h-5 w-5 text-gray-500"
//                       viewBox="0 0 20 20"
//                       fill="currentColor"
//                     >
//                       <path
//                         fillRule="evenodd"
//                         d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
//                         clipRule="evenodd"
//                       />
//                     </svg>
//                     Booking Details
//                   </h4>
//                   <ul className="space-y-3">
//                     {errorDetails.issues.map((issue, index) => (
//                       <li key={index} className="flex gap-3">
//                         <div className="flex-shrink-0 mt-0.5">
//                           <div className="h-2 w-2 rounded-full bg-gray-400"></div>
//                         </div>
//                         <div>
//                           <p className="text-gray-800 text-sm font-medium">
//                             {issue.message}
//                           </p>
//                           <p className="text-gray-500 text-xs mt-0.5">
//                             {issue.friendly_message}
//                           </p>
//                           {issue.checkin_date && (
//                             <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
//                               <svg
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 className="h-3.5 w-3.5"
//                                 fill="none"
//                                 viewBox="0 0 24 24"
//                                 stroke="currentColor"
//                                 strokeWidth={2}
//                               >
//                                 <path
//                                   strokeLinecap="round"
//                                   strokeLinejoin="round"
//                                   d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
//                                 />
//                               </svg>
//                               <span>
//                                 Check-in: {formatDate(issue.checkin_date)}
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                       </li>
//                     ))}
//                   </ul>
//                 </div>
//               )}
//             </div>

//             {/* Footer */}
//             <div className="mt-6 pt-5 border-t border-gray-100">
//               <p className="text-sm text-gray-600">
//                 Need assistance? Contact our support team at
//                 <a
//                   href={`mailto:${hotelInfo?.email || "support@example.com"}`}
//                   className="text-blue-600 hover:underline ml-1"
//                 >
//                   {hotelInfo?.email || "support@example.com"}
//                 </a>
//                 {hotelInfo?.phone && (
//                   <span>
//                     {" "}
//                     or call us at{" "}
//                     <a
//                       href={`tel:${hotelInfo.phone}`}
//                       className="text-blue-600 hover:underline"
//                     >
//                       {hotelInfo.phone}
//                     </a>
//                   </span>
//                 )}
//               </p>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Booking Selection Popup */}
//       {showBookingSelection && bookings && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[80vh] overflow-y-auto shadow-xl">
//             <div className="flex justify-between items-start mb-4">
//               <div>
//                 <h2 className="text-2xl font-bold text-gray-800">
//                   Select Your Booking
//                 </h2>
//                 <p className="text-gray-600 mt-1">
//                   {bookings.primary_guest} • {bookings.bookings[0].guest_email}
//                 </p>
//               </div>
//               <button
//                 onClick={() => setShowBookingSelection(false)}
//                 className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
//               >
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   className="h-6 w-6"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth={2}
//                     d="M6 18L18 6M6 6l12 12"
//                   />
//                 </svg>
//               </button>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
//               {bookings.bookings.map((booking) => (
//                 <div
//                   key={booking.booking_id}
//                   className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all duration-200 hover:border-blue-300"
//                 >
//                   <div className="flex justify-between items-start">
//                     <div>
//                       <div className="flex items-center space-x-2 mb-2">
//                         <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
//                           Reservation Number : {booking.order_id}
//                         </span>
//                       </div>
//                       <h3 className="font-semibold text-lg text-gray-800">
//                         {booking.room_type.name}
//                       </h3>
//                       <p className="text-gray-600 text-sm mt-1">
//                         {formatDate(booking.checkin_date)} -{" "}
//                         {formatDate(booking.checkout_date)} •{" "}
//                         {booking.length_of_stay} night(s)
//                       </p>
//                     </div>
//                   </div>

//                   <div className="mt-4 pt-4 border-t border-gray-100">
//                     <div className="grid grid-cols-2 gap-4">
//                       <div>
//                         <p className="text-sm text-gray-500">Guests</p>
//                         <p className="font-medium">
//                           {booking.number_of_guests}
//                         </p>
//                       </div>
//                       <div>
//                         <p className="text-sm text-gray-500">Status</p>
//                         <p className="font-medium">{booking.status_message}</p>
//                       </div>
//                     </div>
//                   </div>

//                   <button
//                     onClick={() => handleBookingSelection(booking)}
//                     className="mt-4 w-full p-2 bg-black text-white rounded-md hover:text-gray-300 transition-colors"
//                   >
//                     Select This Booking
//                   </button>
//                 </div>
//               ))}
//             </div>

//             <div className="mt-6 pt-6 border-t border-gray-200">
//               <p className="text-sm text-gray-500 text-center">
//                 Please select the booking you want to check in for
//               </p>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default SearchByName;
